import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/hooks/useDeviceLive";
import InfoTip from "@/components/common/InfoTip";

// Stored as fractions/multipliers (what the sizing engine uses); shown to people as 0–100 %.
// `min` keeps efficiency / depth of discharge above 0 so the engine never divides by zero.
const FIELDS = [
  { key: "system_efficiency", label: "System efficiency", help: "How much of the panels' rated power you really get, after heat, dust and wiring losses (typically 75–85 %).",
    toUi: (v) => v * 100, fromUi: (p) => p / 100, min: 1, max: 100,
    info: {
      what: "Solar panels never give their full rated power. Heat, dust, long wires and the inverter all waste some. This is the share you actually get.",
      effect: "Lower it (hot, dusty site) and the report recommends more panels. Raise it and you get fewer, but the promised savings may not be reached.",
    } },
  { key: "inverter_margin", label: "Inverter headroom", help: "Extra capacity added on top of the highest load the meter has measured.",
    toUi: (v) => (v - 1) * 100, fromUi: (p) => 1 + p / 100, min: 0, max: 100,
    info: {
      what: "The inverter should never run at its limit. This adds spare room above the highest load the meter has seen. 25 % means the inverter is sized 25 % bigger than the peak.",
      effect: "Higher is safer for motor starts and surges, but the inverter costs more.",
    } },
  { key: "battery_dod", label: "Battery usable capacity", help: "How much of the battery's label capacity can safely be used (depth of discharge).",
    toUi: (v) => v * 100, fromUi: (p) => p / 100, min: 1, max: 100,
    info: {
      what: "Draining a lithium battery to 0 % shortens its life, so only part of its label capacity is used. This is that usable part.",
      effect: "Higher means a smaller, cheaper battery but faster wear. Lower means a bigger battery.",
    } },
  { key: "backup_fraction", label: "Night backup", help: "How much of the night-time use the battery should cover (100 % = the whole night).",
    toUi: (v) => v * 100, fromUi: (p) => p / 100, min: 0, max: 100,
    info: {
      what: "How much of the client's night-time use the battery should cover. 100 % is the whole night, 50 % is half the night, 0 % means no battery.",
      effect: "Higher gives longer backup with a bigger battery. Lower saves money but covers less of the night.",
    } },
];

const r2 = (n) => Math.round(n * 100) / 100;

/** Percent input: free typing while focused, clamped to 0–100 and committed as a fraction. */
function PercentInput({ field, value, onChange, testid }) {
  const shown = String(r2(field.toUi(value)));
  const [text, setText] = useState(null);   // null = not editing, show the stored value
  const commit = (raw) => {
    setText(raw);
    const n = parseFloat(raw);
    if (!Number.isNaN(n)) onChange(Number(field.fromUi(Math.min(field.max, Math.max(field.min, n))).toFixed(4)));
  };
  return (
    <div className="relative">
      <input data-testid={testid} type="number" inputMode="decimal" min={field.min} max={field.max} step={1}
        className="kl-input-box font-mono text-right pr-9" value={text ?? shown}
        onChange={(e) => commit(e.target.value)} onBlur={() => setText(null)} />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-sm text-kl-mutedLight pointer-events-none">%</span>
    </div>
  );
}

const COMPANY_INFO = {
  name: { what: "The name printed as the signature on every proposal and PDF report sent to clients.", effect: "Changes apply to reports you create or send from now on." },
  email: { what: "The contact address clients see on proposals, and where their replies go.", effect: "Use an inbox your team actually checks." },
};

export default function SizingParams() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const saved = useRef(null);  // JSON of the last server copy, to tell whether the form has unsaved edits
  const pull = (quiet) => api.get("/settings").then((r) => { saved.current = JSON.stringify(r.data); setS(r.data); }).catch((e) => quiet || toast.error(errMsg(e)));
  useEffect(() => { pull(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Live-refresh when settings change elsewhere, unless this form has unsaved edits.
  useInvalidate(["settings"], () => { if (!s || JSON.stringify(s) === saved.current) pull(true); });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.put("/settings", s); saved.current = JSON.stringify(data); setS(data); toast.success("Engine parameters saved"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (!s) return <p className="kl-label">Loading…</p>;

  return (
    <form onSubmit={save} className="animate-rise max-w-3xl" data-testid="sizing-params-form">
      <div className="pb-6 border-b border-kl-black"><p className="kl-label">Engine</p><h2 className="font-display font-light text-3xl mt-1">Global parameters</h2><p className="text-sm text-kl-mutedLight mt-1">Shared by every country. Sun hours, tariff, currency and CO₂ factor are set per country under “Countries & currencies”.</p></div>
      <div className="divide-y divide-kl-lineLight">
        {FIELDS.map((f) => (
          <div key={f.key} className="grid grid-cols-[1fr_180px] gap-6 items-center py-4">
            <div><div className="font-display">{f.label}<InfoTip title={f.label} {...f.info} testid={`info-${f.key}`} /></div><div className="text-xs text-kl-mutedLight">{f.help}</div></div>
            <PercentInput field={f} value={s[f.key]} testid={`param-${f.key}`} onChange={(v) => setS((cur) => ({ ...cur, [f.key]: v }))} />
          </div>
        ))}
        <div className="grid grid-cols-[1fr_180px] gap-6 items-center py-4"><div><div className="font-display">Company name<InfoTip title="Company name" {...COMPANY_INFO.name} testid="info-company-name" /></div><div className="text-xs text-kl-mutedLight">Signature on proposals.</div></div><input data-testid="param-company-name" className="kl-input-box" value={s.company_name} onChange={(e) => setS({ ...s, company_name: e.target.value })} /></div>
        <div className="grid grid-cols-[1fr_180px] gap-6 items-center py-4"><div><div className="font-display">Company email<InfoTip title="Company email" {...COMPANY_INFO.email} testid="info-company-email" /></div><div className="text-xs text-kl-mutedLight">Reply-to on proposals.</div></div><input data-testid="param-company-email" type="email" className="kl-input-box" value={s.company_email} onChange={(e) => setS({ ...s, company_email: e.target.value })} /></div>
      </div>
      <div className="flex justify-end pt-6"><button disabled={busy} className="kl-btn-primary" data-testid="params-save-button">{busy ? "Saving…" : "Save parameters"}</button></div>
    </form>
  );
}
